import React from 'react';
import { Facebook, Instagram, Twitter, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { storeHoursLines, locations } from '../mockData';

const columnShell =
  'group/column rounded-2xl border border-white/[0.09] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-6 sm:p-7 shadow-[0_12px_40px_-16px_rgba(0,0,0,0.65)] ring-1 ring-inset ring-white/[0.04] backdrop-blur-md transition-colors duration-300 hover:border-white/[0.12]';

const FooterHeading = ({ children }) => (
  <h4 className="mb-5 border-b border-green-500/20 pb-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-green-400">
    {children}
  </h4>
);

const footerLinkClass =
  'relative inline-block text-sm text-neutral-400 transition-colors duration-200 before:absolute before:-bottom-0.5 before:left-0 before:h-px before:w-0 before:bg-green-400 before:transition-[width] before:duration-200 hover:text-white hover:before:w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0c0c0e] rounded-sm';

const iconTileClass =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] text-green-400 shadow-inner ring-1 ring-white/10';

const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-gradient-to-br from-[#050505] via-neutral-950 to-black text-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        aria-hidden
        style={{
          backgroundImage: `radial-gradient(ellipse 80% 50% at 50% -20%, rgba(34, 197, 94, 0.14), transparent 55%),
            radial-gradient(circle at 100% 100%, rgba(16, 185, 129, 0.09) 0%, transparent 42%)`,
        }}
      />
      <div className="relative container mx-auto max-w-7xl px-4 sm:px-6 py-14 md:py-20">
        <div className="mb-14 flex flex-col items-center gap-3 text-center">
          <div className="h-px w-16 bg-gradient-to-r from-transparent via-green-500 to-transparent" />
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-green-400/90">
            Tribal Dash
          </p>
          <div className="h-px w-24 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:gap-7 md:grid-cols-2 md:gap-8 lg:grid-cols-12 lg:gap-6">
          {/* About — spans wider feel on large screens */}
          <div className={`lg:col-span-3 ${columnShell}`}>
            <div className="mb-4">
              <img
                src="/tribal-dash-logo.png"
                alt="Tribal Dash"
                className="h-12 w-auto opacity-95 drop-shadow-sm"
              />
            </div>
            <p className="text-sm leading-relaxed text-neutral-400">
              Tribal Dash provides The Hamptons with quality cannabis products at competitive prices. Let
              our courteous staff help you find the perfect products for your needs.
            </p>
          </div>

          <div className={`lg:col-span-2 ${columnShell}`}>
            <FooterHeading>Quick links</FooterHeading>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link to="/menu" className={footerLinkClass}>
                  Shop All
                </Link>
              </li>
              <li>
                <a href="/#categories" className={footerLinkClass}>
                  Categories
                </a>
              </li>
              <li>
                <a href="/#about" className={footerLinkClass}>
                  About Us
                </a>
              </li>
              <li>
                <a href="/#faq" className={footerLinkClass}>
                  FAQ
                </a>
              </li>
              <li>
                <Link to="/driver/login" className={footerLinkClass}>
                  Driver Portal
                </Link>
              </li>
            </ul>
          </div>

          <div className={`lg:col-span-2 ${columnShell}`}>
            <FooterHeading>Categories</FooterHeading>
            <ul className="flex flex-col gap-2.5">
              <li>
                <a href="#flower" className={footerLinkClass}>
                  Flower
                </a>
              </li>
              <li>
                <a href="#pre-rolls" className={footerLinkClass}>
                  Pre-Rolls
                </a>
              </li>
              <li>
                <a href="#edibles" className={footerLinkClass}>
                  Edibles
                </a>
              </li>
              <li>
                <a href="#vaporizers" className={footerLinkClass}>
                  Vaporizers
                </a>
              </li>
              <li>
                <a href="#concentrates" className={footerLinkClass}>
                  Concentrates
                </a>
              </li>
            </ul>
          </div>

          {/* Contact — extra span on lg so hours don’t squeeze */}
          <div className={`lg:col-span-5 ${columnShell}`}>
            <FooterHeading>Contact &amp; hours</FooterHeading>
            <ul className="space-y-5">
              <li className="flex gap-4">
                <span className={iconTileClass}>
                  <MapPin className="h-4 w-4" aria-hidden />
                </span>
                <span className="min-w-0 pt-0.5 text-sm leading-relaxed text-neutral-400">
                  42 Montauk Hwy
                  <br />
                  Southampton, NY 11968
                </span>
              </li>
              <li className="flex items-center gap-4">
                <span className={iconTileClass}>
                  <Phone className="h-4 w-4" aria-hidden />
                </span>
                <a
                  href={`tel:${locations[0].phone.replace(/\D/g, '')}`}
                  className={`${footerLinkClass} text-neutral-300 before:bg-green-400`}
                >
                  {locations[0].phone}
                </a>
              </li>
              <li className="flex items-start gap-4">
                <span className={`${iconTileClass} mt-0.5`}>
                  <Mail className="h-4 w-4" aria-hidden />
                </span>
                <a
                  href="mailto:info@tribaldash.co"
                  className={`${footerLinkClass} break-all text-left text-neutral-300 sm:break-normal`}
                >
                  info@tribaldash.co
                </a>
              </li>
              <li className="flex gap-4 pt-1">
                <span className={`${iconTileClass} self-start`}>
                  <Clock className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 rounded-2xl border border-white/[0.08] bg-black/35 px-5 py-5 shadow-inner ring-1 ring-inset ring-white/[0.04]">
                  <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-green-400">
                    Store hours
                  </p>
                  <dl className="space-y-0">
                    {storeHoursLines.map((row, i) => (
                      <div
                        key={row.days}
                        className={`grid gap-0.5 border-white/[0.06] py-3 sm:grid-cols-[minmax(0,1.1fr)_auto] sm:items-baseline sm:gap-x-4 sm:gap-y-0 ${
                          i < storeHoursLines.length - 1 ? 'border-b' : ''
                        }`}
                      >
                        <dt className="text-[13px] font-medium leading-snug text-neutral-200">{row.days}</dt>
                        <dd className="text-[13px] leading-snug text-neutral-400 tabular-nums sm:text-right whitespace-nowrap">
                          {row.hours}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-8 border-t border-white/10 pt-10 md:flex-row">
          <p className="order-2 text-center text-sm text-neutral-500 md:order-1 md:max-w-md md:text-left">
            © {new Date().getFullYear()} Tribal Dash Cannabis Dispensary. All rights reserved.
          </p>
          <div className="order-1 flex items-center gap-3 md:order-2">
            <a
              href="#facebook"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-neutral-400 transition-all hover:-translate-y-0.5 hover:border-green-500/40 hover:bg-green-500/10 hover:text-green-400 hover:shadow-lg hover:shadow-green-900/20"
              aria-label="Facebook"
            >
              <Facebook className="h-[18px] w-[18px]" />
            </a>
            <a
              href="#instagram"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-neutral-400 transition-all hover:-translate-y-0.5 hover:border-green-500/40 hover:bg-green-500/10 hover:text-green-400 hover:shadow-lg hover:shadow-green-900/20"
              aria-label="Instagram"
            >
              <Instagram className="h-[18px] w-[18px]" />
            </a>
            <a
              href="#twitter"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-neutral-400 transition-all hover:-translate-y-0.5 hover:border-green-500/40 hover:bg-green-500/10 hover:text-green-400 hover:shadow-lg hover:shadow-green-900/20"
              aria-label="Twitter"
            >
              <Twitter className="h-[18px] w-[18px]" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
